import { useEffect, useRef, useState } from "react";
import { Box, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { DigitalDetails } from "../../types/evidence";

type Field = Exclude<keyof DigitalDetails, "emailOpened">;

// `iso` is the shape the API takes for the field.
const FIELDS = [
  { key: "accountCreatedOn", label: "Account created", Picker: DatePicker, iso: "YYYY-MM-DD" },
  { key: "lastLoginAt", label: "Last login", Picker: DateTimePicker, iso: "YYYY-MM-DDTHH:mm" },
] as const;

// As with the contact answer: a failed save puts the server's values back and says so beside them.
const DETAILS_SAVE_FAILED = "Not saved — these are the details the server holds. Type them again.";

const asTyped = (details: DigitalDetails | undefined): Record<Field, string> => ({
  accountCreatedOn: details?.accountCreatedOn ?? "",
  lastLoginAt: details?.lastLoginAt ?? "",
});

// The merchant's portal has no API, so the agent types what its screenshot shows; the digital
// sentences print these dates.
export default function DigitalDetailsFields({ digitalDetails, disabled, onSave, onUnsavedChange }: {
  digitalDetails: DigitalDetails | undefined;
  disabled: boolean;
  /** Resolves false when the server did not take the value. */
  onSave: (value: DigitalDetails) => Promise<boolean>;
  /** The Generate button sits in another card, so the gate it needs is reported upward. */
  onUnsavedChange: (unsaved: boolean) => void;
}) {
  const saved = asTyped(digitalDetails);
  const savedOpened = Boolean(digitalDetails?.emailOpened);
  const [values, setValues] = useState(saved);
  const [opened, setOpened] = useState(savedOpened);
  useEffect(() => { setOpened(savedOpened); }, [savedOpened]);
  const [failed, setFailed] = useState<Field | null>(null);
  const [open, setOpen] = useState<Field | null>(null);
  // A save lands while the agent may already be typing, in this field or the next: only a field still
  // showing the previous saved value takes the new one, so nothing typed since is wiped.
  const { accountCreatedOn, lastLoginAt } = saved;
  const previous = useRef(saved);
  useEffect(() => {
    const next = { accountCreatedOn, lastLoginAt };
    const before = previous.current;
    previous.current = next;
    setValues((current) => ({ ...current, ...Object.fromEntries(FIELDS.filter(({ key }) => current[key] === before[key]).map(({ key }) => [key, next[key]])) }));
  }, [accountCreatedOn, lastLoginAt]);

  const differs = (current: Record<Field, string>) => FIELDS.some(({ key }) => current[key] !== saved[key]);
  // A typed date is saved by the blur that clicking Generate causes, like the ARN. A date picked in an
  // open popup is only committed by the click-away, which lands after the click has generated, so
  // the popup closes Generate while its pick is unsaved; so does a save the server refused.
  const unsaved = failed !== null || (open !== null && differs(values));
  // Gone from the page, the fields hold nothing back from Generate.
  useEffect(() => {
    onUnsavedChange(unsaved);
    return () => onUnsavedChange(false);
  }, [unsaved, onUnsavedChange]);

  // The whole set travels together, so the box saves with what is typed and a field saves with the box.
  const details = (emailOpened: boolean, current = values): DigitalDetails => ({
    accountCreatedOn: current.accountCreatedOn || null, lastLoginAt: current.lastLoginAt || null, emailOpened,
  });

  const saveOpened = (checked: boolean) => {
    setOpened(checked);
    void onSave(details(checked)).then((stored) => { if (!stored) setOpened(savedOpened); });
  };

  // A picker accepts a picked or fully typed date before the state it set has rendered, so it hands over the
  // values it made; the blur that follows sends nothing the save in flight already carries.
  const inFlight = useRef<string | null>(null);
  const save = (field: Field, current = values) => {
    const body = details(opened, current);
    if (!differs(current) || JSON.stringify(body) === inFlight.current) return;
    setFailed(null);
    inFlight.current = JSON.stringify(body);
    void onSave(body)
      .then((stored) => {
        inFlight.current = null;
        if (stored) return;
        setValues((current) => ({ ...current, [field]: saved[field] }));
        setFailed(field);
      });
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <FormControlLabel
        label={<Typography variant="body2">The log shows the email was opened</Typography>}
        sx={{ mb: 0.5 }}
        control={<Checkbox size="small" checked={opened} disabled={disabled} onChange={(event) => saveOpened(event.target.checked)} />}
      />
      <Typography variant="body2" color="text.secondary">Details from the portal</Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {FIELDS.map(({ key, label, Picker, iso }) => {
            // A half-typed date is no value yet: it neither clears the field nor saves.
            const picked = (day: Dayjs | null) => (day === null ? "" : day.isValid() ? day.format(iso) : null);
            return (
              <Picker
                key={key}
                label={label}
                value={values[key] ? dayjs(values[key]) : null}
                onChange={(day: Dayjs | null) => {
                  const value = picked(day);
                  if (value === null) return;
                  setValues((prev) => ({ ...prev, [key]: value }));
                  setFailed(null);
                }}
                onAccept={(day: Dayjs | null) => {
                  const value = picked(day);
                  if (value === null) return;
                  const next = { ...values, [key]: value };
                  setValues(next);
                  save(key, next);
                }}
                onOpen={() => setOpen(key)}
                onClose={() => setOpen(null)}
                disabled={disabled}
                enableAccessibleFieldDOMStructure={false}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    onBlur: () => save(key),
                    error: failed === key,
                    helperText: failed === key ? DETAILS_SAVE_FAILED : undefined,
                  },
                }}
              />
            );
          })}
        </Stack>
      </LocalizationProvider>
    </Box>
  );
}
